import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class StateManagementService {
  private readonly data: Map<string, StateRecord>;

  constructor() {
    this.data = new Map<string, StateRecord>();
  }

  /**
   * Sets a value of an object in the state manager, will create objects to get to the specified path.
   * @param key The path and prefix to set a sub value for. Should be in the form of 'ola:borrowerInfo/addresses/0/address1'
   * @param value The value to set at the specified path in the specified object.
   */
  public appendValue(key: string, value: unknown): void {
    if (key.includes(':')) {
      const root = key.split(':')[0];
      const path = key.split(':')[1].split('/');
      if (!this.data.has(root)) {
        // we should add the object, and assume it's being newly added?
        this.data.set(root, new StateRecord({}));
      }
      const existing = this.data.get(root);
      let active = existing.value;
      for (let i = 0; i < path.length; i++) {
        const part = path[i];
        const next = path[i + 1];
        if (i === path.length - 1) {
          // last segment, we should assign
          active[part] = value;
        } else {
          let match = active[part];
          if (!match && /\d+/.test(next)) {
            match = active[part] = []; // numerical path section next, assume array
          } else if (!match) {
            match = active[part] = {}; // assume object for path with nested properties
          }
          active = match;
        }
      }
      existing.updated();
    }
    // otherwise we have an object that should be set?
  }

  /**
   * Gets a specific field in an object stored in state. If not found, returns undefined.
   * @param key The path and prefix to get a sub value for. Should be in the form of 'ola:borrowerInfo/addresses/0/address1'
   * @returns The value found in the object at the path
   */
  public getValue<T>(key: string): T {
    let result: unknown;
    if (key.includes(':')) {
      const root = key.split(':')[0];
      const path = key.split(':')[1].split('/');
      let value = this.data.get(root)?.value;
      for (const part of path) {
        if (!value) {
          break;
        }
        value = value[part];
      }
      result = value;
    }
    return result as T;
  }

  /**
   * Sets a root object in the state manager.
   * @param key The key of the object to set. This is not a path and is just a key for a root object to store.
   * @param value The value to store - can be any type, but for the future plans of this service, it should be valid JSON.
   */
  public set(key: string, value: unknown): void {
    if (!key) {
      throw Error('There was no key to store the value with.');
    }
    this.data.set(key, new StateRecord(value));
  }

  public get<T>(key: string) {
    if (this.data.has(key)) {
      return this.data.get(key).value as T;
    }
  }

  public delete(key: string): boolean {
    return this.data.delete(key);
  }

  public has(key: string) {
    return this.data.has(key);
  }

  public entries(): IterableIterator<[string, unknown]> {
    const map = this.data;
    return (function* () {
      for (const el of map.entries()) {
        yield [el[0], el[1].value];
      }
    })();
  }

  public get size(): number {
    return this.data.size;
  }

  public keys(): IterableIterator<string> {
    return this.data.keys();
  }

  public clear(): void {
    this.data.clear();
  }

  public [Symbol.iterator](): IterableIterator<[string, unknown]> {
    return this.entries();
  }
}

class StateRecord {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private valueField: any;
  private modifiedAtField: Date;

  constructor(value: unknown) {
    this.valueField = value;
    this.modifiedAtField = new Date();
  }

  public get value(): unknown {
    return this.valueField;
  }

  public set value(value: unknown) {
    this.valueField = value;
    this.modifiedAtField = new Date();
  }

  public get modifiedAt(): Date {
    return this.modifiedAtField;
  }

  public updated(): void {
    this.modifiedAtField = new Date();
  }
}
